// Instrumentación del framework de rollout (§5 del plan). Una sola función aplica las
// dimensiones genéricas `rollout_feature` + `rollout_variant` + `rollout_state` a los 3
// sinks de observabilidad para que los eventos existentes se vuelvan comparables
// treatment/control. Las dimensiones son GENÉRICAS (no `menu_*`) + el slicer `rollout_feature`
// = feature_key, para soportar 2+ features vivas sin mezclar datos bajo una misma dimensión:
//   - GA4: user_properties (viajan en cada evento siguiente).
//   - Clarity: tags de sesión (filtrar replays).
//   - Sentry: tags globales (delta de errores).
//
// Es síncrona y tolerante: si un sink está apagado (tracking off / no-prod), no rompe.

import { ga4SetUserProperties } from '@utils/analytics/ga4';
import { claritySetTags } from '@utils/analytics/clarity';
import {
  captureRolloutDivergence as captureRolloutDivergenceSentry,
  setFeatureVersionTags,
} from '@utils/print/printSentry';
import { MENU_FEATURE_KEY } from './constants';
import type { FeatureMenuState, FeatureVariant } from './types';

// Re-export local: el outbox emite la alerta de divergencia a través del módulo de instrumentación
// del rollout (no directo a printSentry) para mantener una sola superficie de observabilidad del
// framework. Un switch optimista que el backend rechazó/agotó cae aquí tras revertirse.
export const captureRolloutDivergence = captureRolloutDivergenceSentry;

export interface FeatureVersionTags {
  // feature_key del rollout (p.ej. 'menu-v2'). Es el slicer que distingue 2+ features vivas
  // bajo las mismas dimensiones genéricas en GA4/Clarity/Sentry.
  feature_key: string;
  // Variante resuelta (control/treatment). Internamente es la "version" de la feature; se
  // envía bajo la dimensión genérica `rollout_variant`.
  variant: FeatureVariant;
  // Estado per-comercio (subject_state.state). El nombre del VALOR sigue siendo menu_state-ish
  // (never_tried/exploring/...), pero la DIMENSIÓN enviada es genérica `rollout_state`.
  menu_state: FeatureMenuState | null;
}

// Slug del feature_key apto para nombre de dimensión ('menu-v2' → 'menu_v2'). GA4/Clarity no
// admiten '-' cómodamente en nombres de user_property/tag; snake_case es la convención del catálogo.
const dimensionSlug = (featureKey: string): string => featureKey.replace(/[^a-zA-Z0-9]+/g, '_');

export function applyFeatureVersionTags(tags: FeatureVersionTags): void {
  // Dimensiones NAMESPACED por feature (rollout_<key>_variant/state): con 2+ features vivas las
  // dimensiones globales son un slot único (última hidratación gana) y se contaminarían entre sí.
  // Cada feature escribe SOLO las suyas; conviven sin pisarse.
  const slug = dimensionSlug(tags.feature_key);
  const namespaced: Record<string, string | undefined> = {
    [`rollout_${slug}_variant`]: tags.variant,
    [`rollout_${slug}_state`]: tags.menu_state ?? undefined,
  };
  // BACK-COMPAT (solo menu-v2): las dimensiones globales legacy (rollout_feature/variant/state)
  // las leen los dashboards y el guardrail del experimento que está CORRIENDO en prod. menu-v2
  // las sigue escribiendo idénticas; las features nuevas NO (evita la colisión last-write-wins).
  // Retirarlas cuando el experimento de menú concluya y los dashboards migren a las namespaced.
  const legacy =
    tags.feature_key === MENU_FEATURE_KEY
      ? {
          rollout_feature: tags.feature_key,
          rollout_variant: tags.variant,
          rollout_state: tags.menu_state ?? undefined,
        }
      : {};
  const flat = { ...namespaced, ...legacy };
  ga4SetUserProperties(flat);
  claritySetTags(flat);
  setFeatureVersionTags({
    ...namespaced,
    ...(tags.feature_key === MENU_FEATURE_KEY
      ? {
          rollout_feature: tags.feature_key,
          rollout_variant: tags.variant,
          rollout_state: tags.menu_state,
        }
      : {}),
  });
}
